// This is exactly what it sounds like. It's a webpack-specific scanning tool that reads the selected
// context's component directories looking for different versions from the subdirectories named according to version.
//
// The big idea is to allow versioning the formats without requiring hardcoding the versions all over the place.
// The version number is maintained in the backend and brought to state.ui.formSchemaVersion. Each saved/sent form
// also has this value. If it was sent before this was implemented, the form will have a null value in formData.formSchemaVersion.

import { VersionedComponents, VersionedContext } from './constants';
import React from 'react';

export function createVersionedComponentLoader(root, componentName) {
  if (!Object.values(VersionedComponents).includes(componentName)) {
    throw new Error(
      `Missing component definition: ${componentName} in VersionedComponents constant`
    );
  }
  // context for Preview components (they may share names with normal components
  const previewContext = require.context(
    './components/Preview/components/', // this needs to be hardcoded for webpack
    true, // recursive search
    /\/v\d+\/[A-Z][A-Za-z0-9]+\.js$/ // match versioned component files
  );

  const normalComponentContext = require.context(
    './components',
    true,
    /\/v\d+\/[A-Z][A-Za-z0-9]+\.js$/
  );

  const implementationsContext = require.context(
    './impls',
    true,
    /\/v\d+\/[A-Z][A-Za-z0-9]+\.js$/
  );

  const contexts = {
    [VersionedContext.PREVIEW]: previewContext,
    [VersionedContext.COMPONENTS]: normalComponentContext,
    [VersionedContext.IMPLEMENTATIONS]: implementationsContext
  };

  const context = contexts[root];

  const components = {};

  // if the current version isn't found, the latest one is used
  // most of the time the fallback is used as the version number is upped only upon changes
  const latestVersion = { component: null, versionNumber: -1 };

  // if formSchemaVersion is defined as null (sent to SAHA prior to implementation of versioned components)
  // it must load the oldest version
  const oldestVersion = { component: null, versionNumber: 9999 };
  context.keys().forEach(key => {
    const match = key.match(
      new RegExp(`^\\.\\/${componentName}\\/v(\\d+)\\/${componentName}\\.js$`)
    );
    if (match) {
      const version = parseInt(match[1], 10);
      components[version] = context(key).default;

      // here you iterate through all versions (potentially out of order) and keep updating the highest one
      if (latestVersion.versionNumber < version) {
        latestVersion.component = context(key).default;
        latestVersion.versionNumber = version;
      }
      // here you do the same as above but for oldest
      if (oldestVersion.versionNumber > version) {
        oldestVersion.component = context(key).default;
        oldestVersion.versionNumber = version;
      }
    }
  });

  return function getVersionedComponent(version) {
    // this occurs if a versioned component is rendered without the version attribute
    // it SHOULD break the UI
    if (version === undefined) {
      throw new Error(
        `Used versioned component ${componentName} without passing version prop to it.`
      );
    }

    // this occurs when a previously sent form is loaded from a time prior to when versioning existed
    // it should use the oldest available component
    if (version === null) {
      return oldestVersion.component;
    }

    // this occurs when a form is older than the component and thus did not exist
    if (version < oldestVersion.versionNumber) {
      return () => <></>;
    }

    const Component = components[version.toString()] ?? latestVersion.component;
    if (!Component) {
      throw new Error(
        `Version ${version} not found for ${componentName}. Available versions: ${Object.keys(
          components
        ).join(', ')}`
      );
    }
    return Component;
  };
}
